import { useRef, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { CheckCircle2, ChevronDown, CircleDashed, ExternalLink, PencilLine } from "lucide-react";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@monorepo-apps/ui/src/components/collapsible";
import { Input } from "@monorepo-apps/ui/src/components/input";
import { Label } from "@monorepo-apps/ui/src/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@monorepo-apps/ui/src/components/select";
import { Textarea } from "@monorepo-apps/ui/src/components/textarea";

import { useReviewFlow } from "@/application/use-cases/review/use-review-flow-query";
import { useReviewTicketByBoardCode } from "@/application/use-cases/review/use-review-ticket-by-board-code-query";
import { useCreateReviewFlow } from "@/application/use-cases/review/use-create-review-flow-mutation";
import { useUpdateReviewFlow } from "@/application/use-cases/review/use-update-review-flow-mutation";
import { reviewKeys } from "@/application/query-keys/review-keys";
import { useAuth } from "@/presentation/providers/auth.provider";
import { REVIEW_REVIEWERS } from "@/presentation/data/review-reviewers";
import type { ReviewFlowFields } from "@/infrastructure/repositories/review-flows-supabase-repository";
import type { ReviewFlowLink, ReviewFlowPriority } from "@/infrastructure/supabase/database.types";

const PRIORITIES: ReviewFlowPriority[] = ["low", "medium", "high"];
const SYSTEMS = ["buyer", "erp", "pim", "api"];
// Sentinel for the "— clear —" Select item (Radix forbids an empty-string value); it
// maps back to null in each onValueChange so a field can be un-set.
const CLEAR = "__clear__";

/**
 * The block-v review-ticket section at the bottom of the board card drawer.
 *
 * A block-v item is a review item — either GENERATED by /feature-review-sync (it has
 * a review_tickets row) or HAND-ADDED from the board's "Add item" (no row yet). Either
 * way this section holds every field that becomes the Worktracker ticket, editable by
 * can_write/admin (gated by `readOnly` here and by RLS in the DB). For a hand-added
 * item the fields start empty and the first save CREATES the review_flows row, keyed
 * on the board code — so you can author a review manually, not only consume automated
 * flows. Sending it to the tracker is the driver's `sync` step.
 */
interface BoardReviewTicketSectionProps {
  boardCode: string;
  boardTitle: string;
  readOnly: boolean;
}

export function BoardReviewTicketSection({
  boardCode,
  boardTitle,
  readOnly,
}: BoardReviewTicketSectionProps) {
  const { session } = useAuth();
  const ticketQ = useReviewTicketByBoardCode(boardCode);
  const ticket = ticketQ.data ?? null;

  // The flow id: a generated item carries it on its ticket; a hand-added item is keyed
  // on its board code. Wait for the ticket lookup to settle so we don't fetch on the
  // wrong key first.
  const flowId = ticket ? ticket.flow_id : ticketQ.isLoading ? null : boardCode;
  const flowQ = useReviewFlow(flowId);
  const flow = flowQ.data ?? null;

  const update = useUpdateReviewFlow();
  const create = useCreateReviewFlow();
  const queryClient = useQueryClient();
  // Serialize saves for this flow: field edits fire save() in quick succession, and a
  // bare `if (flow)` off a stale render would let two of them both hit the create branch
  // and collide on the flow_id primary key. Chaining + a fresh cache read means the row
  // is created exactly once, then every later patch is an ordered update.
  const saveChain = useRef<Promise<unknown>>(Promise.resolve());
  // Fields whose save is pending/failed. A failed save stays here (not discarded) so
  // the next save retries it; each field clears only once it actually persists.
  const dirty = useRef<ReviewFlowFields>({});

  const save = (patch: ReviewFlowFields) => {
    if (readOnly || !flowId) return;
    const updated_by = session?.user?.id ?? null;
    dirty.current = { ...dirty.current, ...patch };
    saveChain.current = saveChain.current
      .then(async () => {
        const toSend = { ...dirty.current };
        if (!Object.keys(toSend).length) return;
        // Freshest existence check: a create earlier in the chain has, by now, written
        // the row into the query cache — so a rapid follow-up patches instead of racing.
        const existing = queryClient.getQueryData(reviewKeys.flow(flowId));
        if (existing) await update.mutateAsync({ flowId, patch: { ...toSend, updated_by } });
        else
          await create.mutateAsync({
            flow_id: flowId,
            title: toSend.title ?? boardTitle,
            ...toSend,
            updated_by,
          });
        // Persisted — drop the fields we just sent, unless a newer edit re-dirtied them.
        for (const key of Object.keys(toSend) as (keyof ReviewFlowFields)[])
          if (dirty.current[key] === toSend[key]) delete dirty.current[key];
      })
      .catch(() => {
        /* dirty.current keeps the failed fields for the next save; error surfaced via
           update.isError / create.isError */
      });
  };
  const saveError = update.isError || create.isError;

  const linesToArray = (v: string) =>
    v
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
  const codesToArray = (v: string) => v.split(/[\s,]+/).filter(Boolean);
  const linksToText = (links: ReviewFlowLink[]) =>
    links.map((l) => `${l.label} | ${l.path}`).join("\n");
  const parseLinks = (v: string, system: string): ReviewFlowLink[] =>
    linesToArray(v).map((line) => {
      const [label, path] = line.split("|").map((s) => s.trim());
      return { system, path: path ?? "", label: label ?? "" };
    });

  const loading = ticketQ.isLoading || flowQ.isLoading;
  const errored = ticketQ.isError || flowQ.isError;
  const isDraft = !ticket; // hand-added / not yet sent to the tracker

  return (
    <Collapsible defaultOpen className="border-border/60 rounded-md border">
      <CollapsibleTrigger className="group flex w-full items-center justify-between gap-2 px-3.5 py-2.5 text-left">
        <span className="flex items-center gap-2">
          <span className="eyebrow">Review ticket</span>
          {ticket ? (
            ticket.ticket_opened ? (
              <span className="inline-flex items-center gap-1 text-[10.5px] font-medium text-[var(--rag-green)]">
                <CheckCircle2 className="size-3" /> Sent to Tracker
              </span>
            ) : (
              <span className="text-muted-foreground inline-flex items-center gap-1 text-[10.5px]">
                <CircleDashed className="size-3" /> Not opened yet
              </span>
            )
          ) : (
            <span className="text-muted-foreground inline-flex items-center gap-1 text-[10.5px]">
              <PencilLine className="size-3" /> Draft — not sent
            </span>
          )}
        </span>
        <ChevronDown className="text-muted-foreground size-4 transition-transform group-data-[state=closed]:-rotate-90" />
      </CollapsibleTrigger>

      <CollapsibleContent className="border-border/60 flex flex-col gap-4 border-t px-3.5 py-3.5">
        {errored ? (
          <p className="text-muted-foreground text-[11.5px] italic">
            Could not load review data (the review tables may not be migrated yet).
          </p>
        ) : loading ? (
          <p className="text-muted-foreground text-[11.5px] italic">Loading review data…</p>
        ) : (
          <>
            {/* Ticket linkage — driver-owned, read-only. Present only once sent. */}
            {ticket ? (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11.5px]">
                <MetaBit label="Flow" value={<span className="font-mono">{ticket.flow_id}</span>} />
                {ticket.ticket_id && ticket.ticket_url ? (
                  <a
                    href={ticket.ticket_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary inline-flex items-center gap-1 font-mono hover:underline"
                  >
                    {ticket.ticket_id}
                    <ExternalLink className="size-3" />
                  </a>
                ) : null}
              </div>
            ) : (
              <p className="text-muted-foreground text-[11px]">
                Hand-authored review item. Fill the fields below; it goes to Worktracker on the next
                <span className="font-mono"> sync</span>.
              </p>
            )}

            {/* The editable content — every field that becomes the ticket. */}
            <div className="flex flex-col gap-3.5">
              <Row label="Title">
                <Input
                  key={`${flowId}-title`}
                  defaultValue={flow?.title ?? boardTitle}
                  disabled={readOnly}
                  className="h-9 text-[13px]"
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v && v !== (flow?.title ?? boardTitle)) save({ title: v });
                  }}
                />
              </Row>

              <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
                <Row label="Reviewer (sets the ticket assignee)">
                  <Select
                    value={flow?.reviewer_key ?? undefined}
                    disabled={readOnly}
                    onValueChange={(v) => save({ reviewer_key: v === CLEAR ? null : v })}
                  >
                    <SelectTrigger className="h-9 w-full text-[13px]">
                      <SelectValue placeholder="Unassigned" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={CLEAR} className="text-muted-foreground text-[13px]">
                        — clear —
                      </SelectItem>
                      {REVIEW_REVIEWERS.map((r) => (
                        <SelectItem key={r.key} value={r.key} className="text-[13px]">
                          {r.name}
                        </SelectItem>
                      ))}
                      {flow?.reviewer_key &&
                        flow.reviewer_key !== CLEAR &&
                        !REVIEW_REVIEWERS.some((r) => r.key === flow.reviewer_key) && (
                          <SelectItem
                            value={flow.reviewer_key}
                            disabled
                            className="text-muted-foreground text-[13px]"
                          >
                            {flow.reviewer_key} (unknown)
                          </SelectItem>
                        )}
                    </SelectContent>
                  </Select>
                </Row>

                <Row label="Priority">
                  <Select
                    value={flow?.priority ?? undefined}
                    disabled={readOnly}
                    onValueChange={(v) =>
                      save({ priority: v === CLEAR ? null : (v as ReviewFlowPriority) })
                    }
                  >
                    <SelectTrigger className="h-9 w-full text-[13px] capitalize">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={CLEAR} className="text-muted-foreground text-[13px]">
                        — clear —
                      </SelectItem>
                      {PRIORITIES.map((p) => (
                        <SelectItem key={p} value={p} className="text-[13px] capitalize">
                          {p}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Row>

                <Row label="System (drives the deep-link module)">
                  <Select
                    value={flow?.system ?? undefined}
                    disabled={readOnly}
                    onValueChange={(v) => {
                      const sys = v === CLEAR ? null : v;
                      // Realign existing deep-link systems with the flow's System, or a
                      // link keeps its old system and the next sync builds a deep-link
                      // for the wrong module.
                      const links = (flow?.links ?? []).map((l) => ({ ...l, system: sys ?? "" }));
                      save({ system: sys, links });
                    }}
                  >
                    <SelectTrigger className="h-9 w-full text-[13px]">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={CLEAR} className="text-muted-foreground text-[13px]">
                        — clear —
                      </SelectItem>
                      {SYSTEMS.map((s) => (
                        <SelectItem key={s} value={s} className="text-[13px]">
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Row>

                <Row label="Area (label)">
                  <Input
                    key={`${flowId}-area`}
                    defaultValue={flow?.area ?? ""}
                    disabled={readOnly}
                    placeholder="e.g. Catalog Sync"
                    className="h-9 text-[13px]"
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      if (v !== (flow?.area ?? "")) save({ area: v || null });
                    }}
                  />
                </Row>
              </div>

              <Row label="Covered board items (BF codes)">
                <Input
                  key={`${flowId}-codes`}
                  defaultValue={(flow?.board_codes ?? []).join(" ")}
                  disabled={readOnly}
                  placeholder="BF-053 BF-054"
                  className="h-9 font-mono text-[12.5px]"
                  onBlur={(e) => {
                    const next = codesToArray(e.target.value);
                    if (JSON.stringify(next) !== JSON.stringify(flow?.board_codes ?? []))
                      save({ board_codes: next });
                  }}
                />
              </Row>

              <Row label="Description">
                <Textarea
                  key={`${flowId}-desc`}
                  dir="auto"
                  defaultValue={flow?.description ?? ""}
                  disabled={readOnly}
                  className="min-h-20 text-[12.5px]"
                  onBlur={(e) => {
                    if (e.target.value !== (flow?.description ?? ""))
                      save({ description: e.target.value });
                  }}
                />
              </Row>

              <Row label="Partial-scope note (shown as “PARTLY LIVE”)">
                <Textarea
                  key={`${flowId}-partial`}
                  dir="auto"
                  defaultValue={flow?.partial ?? ""}
                  disabled={readOnly}
                  placeholder="Leave empty if fully live."
                  className="min-h-14 text-[12.5px]"
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v !== (flow?.partial ?? "")) save({ partial: v || null });
                  }}
                />
              </Row>

              <Row label="Steps — one per line">
                <Textarea
                  key={`${flowId}-steps`}
                  dir="auto"
                  defaultValue={(flow?.steps ?? []).join("\n")}
                  disabled={readOnly}
                  className="min-h-20 text-[12.5px]"
                  onBlur={(e) => {
                    const next = linesToArray(e.target.value);
                    if (JSON.stringify(next) !== JSON.stringify(flow?.steps ?? []))
                      save({ steps: next });
                  }}
                />
              </Row>

              <Row label="Questions — one per line">
                <Textarea
                  key={`${flowId}-asks`}
                  dir="auto"
                  defaultValue={(flow?.asks ?? []).join("\n")}
                  disabled={readOnly}
                  className="min-h-20 text-[12.5px]"
                  onBlur={(e) => {
                    const next = linesToArray(e.target.value);
                    if (JSON.stringify(next) !== JSON.stringify(flow?.asks ?? []))
                      save({ asks: next });
                  }}
                />
              </Row>

              <Row label="Deep links — one per line as “Label | /path” (uses the System above)">
                <Textarea
                  key={`${flowId}-links`}
                  defaultValue={linksToText(flow?.links ?? [])}
                  disabled={readOnly}
                  placeholder="Item list | /app/item"
                  className="min-h-16 font-mono text-[12px]"
                  onBlur={(e) => {
                    // Use the pending System (if a change hasn't settled yet) so links
                    // aren't parsed against a stale flow.system.
                    const sys =
                      "system" in dirty.current
                        ? (dirty.current.system ?? "")
                        : (flow?.system ?? "");
                    const next = parseLinks(e.target.value, sys);
                    if (JSON.stringify(next) !== JSON.stringify(flow?.links ?? []))
                      save({ links: next });
                  }}
                />
              </Row>

              {saveError ? (
                <p className="text-destructive text-[11px]">
                  Could not save — you may not have edit permission.
                </p>
              ) : null}
              {readOnly ? (
                <p className="text-muted-foreground text-[10.5px] italic">
                  View-only — editing is limited to the delivery team.
                </p>
              ) : (
                <p className="text-muted-foreground text-[10.5px] italic">
                  Edits save on blur.{" "}
                  {isDraft
                    ? "This item is authored here and sent to Worktracker on the next sync."
                    : "Changes feed the next dated review pack and re-sync."}
                </p>
              )}
            </div>
          </>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}

interface MetaBitProps {
  label: string;
  value: ReactNode;
}

function MetaBit({ label, value }: MetaBitProps) {
  return (
    <span className="text-muted-foreground">
      {label}: <span className="text-foreground">{value}</span>
    </span>
  );
}

interface RowProps {
  label: string;
  children: ReactNode;
}

function Row({ label, children }: RowProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="eyebrow">{label}</Label>
      {children}
    </div>
  );
}
